import React from "react";
import { Box, Card, CardMedia, Chip, Divider, Grid, Typography } from "@mui/material";
import { translationService } from "../../services/translation/TranslationService";
import { OcrCodedField } from "./detectionProvenance";

const t = (key: string) => translationService.translate(key);

/**
 * One labelled value from an observation, with a marker when a human set it.
 *
 * A marked field is a label rather than a colour on the value, because the PDF export is a
 * greyscale-safe capture of this DOM: a colour alone would not survive it, nor serve a
 * colour-blind reader.
 */
const renderField = (label: string, value?: string | number, edited?: boolean) => {
  if (value === undefined || value === null || value === "") return <></>;
  return (
    <Grid container spacing={1} alignItems="flex-start">
      <Grid item>
        <Typography variant="body2" fontWeight="bold">
          {label}:
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="body2">{value}</Typography>
      </Grid>
      {edited && (
        <Grid item>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={t("comparison.detection.field-edited")}
          />
        </Grid>
      )}
    </Grid>
  );
};

export interface DetectionCardProps {
  /** Chips above the fields: what the reviewer did to the row, and their verdict on it. */
  badges?: React.ReactNode;
  /** Edit / delete, top right. Omitted on a read-only view. */
  actions?: React.ReactNode;
  /** Already formatted — the two sides encode a video position differently. */
  time?: string;
  distance?: string | number;
  mainCode?: string;
  c1?: string;
  c2?: string;
  sa?: string;
  q1?: string;
  q2?: string;
  positionFrom?: string;
  positionTo?: string;
  text?: string;
  /** Coded fields a human changed, from {@link changedCodedFields}. */
  changed?: Set<OcrCodedField>;
  /** The frame, when there is one. */
  frameUrl?: string;
  /** Something to draw in place of the plain frame — the OCR side's segmentation overlay. */
  frameNode?: React.ReactNode;
}

/**
 * One observation, as both the Inspection Insights and Video Analysis lists show it.
 *
 * The two sides deliberately render the SAME card: a reviewer moving between the tabs is reading
 * one inspection, and a different layout per tab made the same observation look like two
 * different kinds of record. Half the card is the coded observation, half is the frame it was
 * read from, and the field order is the order the standards documents list them in.
 *
 * Presentational only — no store, no service, no edit logic. Both lists own their own data and
 * hand this what to draw, which is what lets one component serve a review DTO and an ocr.json
 * display row without knowing that either shape exists.
 *
 * `id="pdf-content-card"` is what the PDF export collects (see OcrService.downloadPdf), so a card
 * rendered here lands in the exported document like any other.
 */
export const DetectionCard: React.FC<DetectionCardProps> = ({
  badges,
  actions,
  time,
  distance,
  mainCode,
  c1,
  c2,
  sa,
  q1,
  q2,
  positionFrom,
  positionTo,
  text,
  changed,
  frameUrl,
  frameNode,
}) => {
  const wasChanged = (field: OcrCodedField) => changed?.has(field) ?? false;

  return (
    <Card
      sx={{
        display: "flex",
        mb: 2,
        flexDirection: "row",
        borderRadius: 2,
        border: "1px solid black",
        boxShadow: 8,
        overflow: "hidden",
      }}
      id="pdf-content-card"
    >
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Box sx={{ p: 2 }}>
            {badges && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>{badges}</Box>
            )}

            {actions && (
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}
                data-html2canvas-ignore
              >
                {actions}
              </Box>
            )}

            {renderField(t("ocrService.inspectionInfo.time"), time)}
            {renderField(
              t("ocrService.inspectionInfo.distance"),
              distance,
              wasChanged("distance")
            )}
            {renderField("Main Code", mainCode, wasChanged("mainCode"))}

            <Divider sx={{ my: 1, borderBottom: "1px solid black", borderStyle: "dashed" }} />

            {renderField("C1", c1, wasChanged("c1"))}
            {renderField("C2", c2, wasChanged("c2"))}
            {renderField("SA", sa, wasChanged("sa"))}
            {renderField("Q1", q1, wasChanged("q1"))}
            {renderField("Q2", q2, wasChanged("q2"))}
            {renderField(
              t("ocrService.inspectionInfo.positionFrom"),
              positionFrom,
              wasChanged("positionFrom")
            )}
            {renderField(
              t("ocrService.inspectionInfo.positionTo"),
              positionTo,
              wasChanged("positionTo")
            )}

            <Divider sx={{ my: 1, borderBottom: "1px solid black", borderStyle: "dashed" }} />

            {renderField(t("ocrService.inspectionInfo.text"), text)}
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 2 }}
        >
          {frameNode ??
            (frameUrl ? (
              <CardMedia
                component="img"
                image={frameUrl}
                alt="frame"
                sx={{ width: "100%", height: "auto", objectFit: "cover" }}
              />
            ) : (
              // Said in words. Rendering the img with an empty src produced a broken-image icon,
              // which reads as "the picture failed to load" rather than "there is no picture".
              <Typography variant="caption" color="text.secondary">
                {t("comparison.detection.no-frame")}
              </Typography>
            ))}
        </Grid>
      </Grid>
    </Card>
  );
};

export default DetectionCard;
